import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Box from "@mui/material/Box";
import DialogActions from "@mui/material/DialogActions";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useTranslationApp } from "../../../lib/i18next";
import ModalBasicLayout from "../../../components/Modal/ModalBasicLayout";
import TextField from "../../../components/Form/TextField";
import ContainerModalActions from "../../../components/Containers/ContainerModalActions";
import {
  BUTTON_CANCEL,
  BUTTON_CONTINUE,
  ERROR_RESIZE_DIMENSION_MAX_EDGE,
  FIELD_REQUIRED,
  TEMPLATES_CONTINUOUS_SETUP_FORMAT,
  TEMPLATES_CONTINUOUS_SETUP_SLIDES,
  TEMPLATES_CONTINUOUS_SETUP_TITLE,
  TEMPLATES_CUSTOM_FORMAT,
  TEMPLATES_CUSTOM_HEIGHT,
  TEMPLATES_CUSTOM_WIDTH,
  TEMPLATES_STRIP_FORMAT_TOO_LARGE,
} from "../../../i18n/keysTranslations";
import { CREATIVE_TEMPLATE_ARTBOARD_PRESETS } from "../../../utils/constants";
import {
  CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MAX,
  CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MIN,
} from "../../../utils/creativeTemplateLayout";
import { RESIZE_MAX_EDGE } from "../../../utils/resizeCustomDimensions";
import { artboardChipSx } from "./ArtboardSwitcher";
import { nextCustomArtboardID } from "../utils/artboards";
import { buildStripSize, isStripWithinLimits } from "../utils/continuousStrip";

const CUSTOM_FORMAT_KEY = "custom";
const SLIDE_COUNT_OPTIONS = Array.from(
  {
    length:
      CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MAX -
      CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MIN +
      1,
  },
  (_, index) => CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MIN + index
);
const PRESET_OPTIONS = Object.values(CREATIVE_TEMPLATE_ARTBOARD_PRESETS);
const DEFAULT_PRESET_ID = CREATIVE_TEMPLATE_ARTBOARD_PRESETS.square.id;

const parseDimension = (value) => {
  const num = Math.round(Number(value));
  if (!Number.isFinite(num) || num <= 0) return { valid: false };
  return { valid: true, value: num, exceedsMax: num > RESIZE_MAX_EDGE };
};

const pxEndAdornment = (
  <InputAdornment position="end" sx={{ ml: 0, mt: 0.25 }}>
    <Typography
      variant="caption"
      sx={{ color: "text.secondary", fontSize: 14, fontWeight: 500 }}
    >
      px
    </Typography>
  </InputAdornment>
);

const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{ fontWeight: 600, color: "text.secondary", letterSpacing: 0.2 }}
  >
    {children}
  </Typography>
);

const ContinuousSetupModal = ({ open, onClose, onConfirm }) => {
  const { t } = useTranslationApp();
  const theme = useTheme();
  const [slideCount, setSlideCount] = useState(
    CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MIN
  );
  const [formatKey, setFormatKey] = useState(DEFAULT_PRESET_ID);
  const {
    control,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    defaultValues: { width: "", height: "" },
    mode: "onChange",
  });

  useEffect(() => {
    if (!open) return;
    setSlideCount(CREATIVE_TEMPLATE_CONTINUOUS_SLIDE_MIN);
    setFormatKey(DEFAULT_PRESET_ID);
    reset({ width: "", height: "" });
  }, [open, reset]);

  const isCustom = formatKey === CUSTOM_FORMAT_KEY;
  const parsedWidth = parseDimension(watch("width"));
  const parsedHeight = parseDimension(watch("height"));
  const customSize =
    parsedWidth.valid &&
    parsedHeight.valid &&
    !parsedWidth.exceedsMax &&
    !parsedHeight.exceedsMax
      ? { width: parsedWidth.value, height: parsedHeight.value }
      : null;

  const selectedPreset = isCustom
    ? null
    : CREATIVE_TEMPLATE_ARTBOARD_PRESETS[formatKey];
  const slideSize = isCustom ? customSize : selectedPreset;
  const isStripAllowed = Boolean(
    slideSize &&
      isStripWithinLimits(
        buildStripSize({
          slideWidth: slideSize.width,
          slideHeight: slideSize.height,
          slideCount,
        })
      )
  );
  const showTooLarge = Boolean(slideSize) && !isStripAllowed;

  const isPresetAllowed = (preset) =>
    isStripWithinLimits(
      buildStripSize({
        slideWidth: preset.width,
        slideHeight: preset.height,
        slideCount,
      })
    );

  const requiredRules = {
    required: { value: true, message: t(FIELD_REQUIRED) },
    validate: (value) => {
      const parsed = parseDimension(value);
      if (parsed.valid && parsed.exceedsMax) {
        return t(ERROR_RESIZE_DIMENSION_MAX_EDGE, { max: RESIZE_MAX_EDGE });
      }
      return true;
    },
  };

  const onSubmit = () => {
    if (!slideSize || !isStripAllowed) return;
    const format = isCustom
      ? {
          id: nextCustomArtboardID({
            width: slideSize.width,
            height: slideSize.height,
            artboards: {},
          }),
          width: slideSize.width,
          height: slideSize.height,
          label: `${slideSize.width}×${slideSize.height}`,
        }
      : {
          id: selectedPreset.id,
          width: selectedPreset.width,
          height: selectedPreset.height,
          label: selectedPreset.label,
        };
    onConfirm({ slideCount, format });
  };

  return (
    <ModalBasicLayout
      modalOpen={open}
      onCloseModal={onClose}
      title={t(TEMPLATES_CONTINUOUS_SETUP_TITLE)}
      ariaLabel={t(TEMPLATES_CONTINUOUS_SETUP_TITLE)}
      maxWidth="xs"
      fullWidth
      disableScrollLock
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <SectionLabel>{t(TEMPLATES_CONTINUOUS_SETUP_SLIDES)}</SectionLabel>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            {SLIDE_COUNT_OPTIONS.map((count) => (
              <Box
                key={count}
                component="button"
                type="button"
                onClick={() => setSlideCount(count)}
                sx={artboardChipSx({
                  active: count === slideCount,
                  disabled: false,
                  theme,
                })}
              >
                {count}
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          <SectionLabel>{t(TEMPLATES_CONTINUOUS_SETUP_FORMAT)}</SectionLabel>
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
            {PRESET_OPTIONS.map((preset) => {
              const allowed = isPresetAllowed(preset);
              return (
                <Box
                  key={preset.id}
                  component="button"
                  type="button"
                  disabled={!allowed}
                  onClick={() => setFormatKey(preset.id)}
                  sx={artboardChipSx({
                    active: formatKey === preset.id,
                    disabled: !allowed,
                    theme,
                  })}
                >
                  {preset.label}
                </Box>
              );
            })}
            <Box
              component="button"
              type="button"
              onClick={() => setFormatKey(CUSTOM_FORMAT_KEY)}
              sx={artboardChipSx({
                active: isCustom,
                disabled: false,
                theme,
              })}
            >
              {t(TEMPLATES_CUSTOM_FORMAT)}
            </Box>
          </Box>
        </Box>
        {isCustom ? (
          <Box sx={{ display: "flex", gap: 1.5 }}>
            <TextField
              name="width"
              control={control}
              errors={errors}
              label={t(TEMPLATES_CUSTOM_WIDTH)}
              variant="filled"
              type="number"
              fullWidth
              autoFocus
              inputProps={{ min: 1, max: RESIZE_MAX_EDGE }}
              InputProps={{ endAdornment: pxEndAdornment }}
              rules={requiredRules}
            />
            <TextField
              name="height"
              control={control}
              errors={errors}
              label={t(TEMPLATES_CUSTOM_HEIGHT)}
              variant="filled"
              type="number"
              fullWidth
              inputProps={{ min: 1, max: RESIZE_MAX_EDGE }}
              InputProps={{ endAdornment: pxEndAdornment }}
              rules={requiredRules}
            />
          </Box>
        ) : null}
        {showTooLarge ? (
          <Typography variant="caption" color="error">
            {t(TEMPLATES_STRIP_FORMAT_TOO_LARGE)}
          </Typography>
        ) : null}
      </Box>
      <DialogActions sx={{ px: { xs: 3, sm: 5 }, pb: 3, mt: 0.5 }}>
        <ContainerModalActions
          hasPadding={false}
          propsSecondaryButton={{
            showButton: true,
            text: BUTTON_CANCEL,
            onClick: onClose,
          }}
          propsPrimaryButton={{
            showButton: true,
            text: BUTTON_CONTINUE,
            onClick: onSubmit,
            disabled: !isStripAllowed,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ContinuousSetupModal;
